import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import config from "../../config/config";
import EmployerPageFrame from "../../components/employer/EmployerPageFrame";
import { Button } from "../../components/ui/button";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["clean"],
  ],
};

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</label>
      {children}
    </div>
  );
}

export default function StbEditStellenanzeige() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [baseData, setBaseData] = useState({
    employerId: "",
    title: "",
    description: "",
    skills: "",
    region: "",
    specialStatus: 0,
    status: "inactive",
  });
  const [langData, setLangData] = useState({
    job_description: "",
    qualifications: "",
    additional_info: "",
    beruf_id: "",
    arbeitsform_id: "",
    language: "de",
  });
  const [berufOptions, setBerufOptions] = useState([]);
  const [arbeitsformOptions, setArbeitsformOptions] = useState([]);

  useEffect(() => {
    document.title = "Experts4Europe | Legacy Edit Anzeige";

    axios.get(`${config.API_BASE_URL}/api/berufsbezeichnung`)
      .then((response) => {
        const data = Array.isArray(response.data)
          ? response.data.map((item) => ({ id: item.id, name: item.beruf }))
          : [];
        setBerufOptions(data);
      })
      .catch(() => setError("Berufsbezeichnungen konnten nicht geladen werden."));

    axios.get(`${config.API_BASE_URL}/api/arbeitsform`)
      .then((response) => {
        const data = Array.isArray(response.data)
          ? response.data.map((item) => ({ id: item.id, name: item.arbeitsform }))
          : [];
        setArbeitsformOptions(data);
      })
      .catch(() => setError("Arbeitsformen konnten nicht geladen werden."));
  }, []);

  useEffect(() => {
    if (!id || id === "new") return;

    axios.get(`${config.API_BASE_URL}/api/stb/suchanzeigen/${id}`)
      .then((response) => setBaseData(response.data))
      .catch(() => setError("Basisdaten konnten nicht geladen werden."));

    axios.get(`${config.API_BASE_URL}/api/stb/suchanzeige_lang/${id}?lang=de`)
      .then((response) => setLangData(response.data))
      .catch(() => setError("Sprachdaten konnten nicht geladen werden."));
  }, [id]);

  const handleBaseChange = (event) => {
    const { name, value, type, checked } = event.target;
    setBaseData((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
    }));
  };

  const handleLangChange = (event) => {
    const { name, value } = event.target;
    setLangData((previous) => ({ ...previous, [name]: value }));
  };

  const handleSave = async () => {
    try {
      const axiosConfig = { headers: { "Content-Type": "application/json" } };

      if (id && id !== "new") {
        await axios.put(`${config.API_BASE_URL}/api/stb/suchanzeigen/${id}`, baseData, axiosConfig);
        await axios.put(`${config.API_BASE_URL}/api/stb/suchanzeige_lang/${id}`, langData, axiosConfig);
      } else {
        const resBase = await axios.post(`${config.API_BASE_URL}/api/stb/suchanzeigen`, baseData, axiosConfig);
        const newId = resBase.data.id;
        await axios.post(`${config.API_BASE_URL}/api/stb/suchanzeige_lang`, { ...langData, suchanzeige_id: newId }, axiosConfig);
      }

      navigate("/dashboard/employer");
    } catch (saveError) {
      console.error("Fehler beim Speichern der Anzeige:", saveError);
      setError("Fehler beim Speichern der Anzeige.");
    }
  };

  return (
    <EmployerPageFrame
      title="Legacy Edit Anzeige"
      subtitle="Bestehender Altpfad bleibt funktional erhalten, laeuft aber jetzt in derselben Employer-Vollflaechenlogik statt im isolierten Dark-Container."
      actions={(
        <>
          <Button variant="outline" className="dashboard-action-button dashboard-action-button-secondary" onClick={() => navigate(-1)}>
            Abbrechen
          </Button>
          <Button className="dashboard-action-button" onClick={handleSave}>
            Speichern
          </Button>
        </>
      )}
      contentClassName="space-y-4"
    >
      {error ? <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</p> : null}

      <section className="dashboard-card-shell">
        <div className="dashboard-section-head">
          <div>
            <span className="dashboard-kicker">Legacy Editor</span>
            <h2 className="dashboard-card-title">Basisdaten</h2>
          </div>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <Field label="Jobtitel">
            <input name="title" value={baseData.title} onChange={handleBaseChange} className="dashboard-field" />
          </Field>
          <Field label="Region">
            <input name="region" value={baseData.region} onChange={handleBaseChange} className="dashboard-field" />
          </Field>
          <Field label="Kurzbeschreibung">
            <textarea name="description" value={baseData.description} onChange={handleBaseChange} rows={4} className="dashboard-field dashboard-textarea" />
          </Field>
          <Field label="Skills">
            <textarea name="skills" value={baseData.skills} onChange={handleBaseChange} rows={4} className="dashboard-field dashboard-textarea" />
          </Field>
        </div>
      </section>

      <section className="dashboard-card-shell">
        <div className="dashboard-section-head">
          <div>
            <span className="dashboard-kicker">Sprachdaten</span>
            <h2 className="dashboard-card-title">Ausfuehrliche Anzeige</h2>
          </div>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <Field label="Berufsbezeichnung">
            <select name="beruf_id" value={langData.beruf_id} onChange={handleLangChange} className="dashboard-field">
              <option value="">Bitte waehlen</option>
              {berufOptions.map((option) => (
                <option key={option.id} value={option.id}>{option.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Arbeitsform">
            <select name="arbeitsform_id" value={langData.arbeitsform_id} onChange={handleLangChange} className="dashboard-field">
              <option value="">Bitte waehlen</option>
              {arbeitsformOptions.map((option) => (
                <option key={option.id} value={option.id}>{option.name}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="mt-4 space-y-4">
          <Field label="Jobbeschreibung">
            <ReactQuill value={langData.job_description || ""} onChange={(value) => setLangData((previous) => ({ ...previous, job_description: value }))} modules={quillModules} theme="snow" />
          </Field>
          <Field label="Qualifikationen">
            <ReactQuill value={langData.qualifications || ""} onChange={(value) => setLangData((previous) => ({ ...previous, qualifications: value }))} modules={quillModules} theme="snow" />
          </Field>
          <Field label="Weitere Informationen">
            <ReactQuill value={langData.additional_info || ""} onChange={(value) => setLangData((previous) => ({ ...previous, additional_info: value }))} modules={quillModules} theme="snow" />
          </Field>
        </div>
      </section>
    </EmployerPageFrame>
  );
}
